import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { adminService, type FamilySpace } from '@/services/adminService';
import { useAuth } from '@/hooks/useAuth';

function inviteLink(code: string) {
  return `${window.location.origin}/signup?invite=${encodeURIComponent(code)}`;
}

export default function AdminFamiliesPage() {
  const { user } = useAuth();
  const [families, setFamilies] = useState<FamilySpace[]>([]);
  const [maxFamilies, setMaxFamilies] = useState(3);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [copiedId, setCopiedId] = useState('');

  const canCreate = families.length < maxFamilies;
  const slotsText = useMemo(() => `${families.length} / ${maxFamilies} 가족 공간 사용 중`, [families.length, maxFamilies]);

  const load = async () => {
    setLoading(true);
    try {
      const response = await adminService.getFamilies();
      setFamilies(response.families);
      setMaxFamilies(response.maxFamilies);
      setError('');
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '가족 공간 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const copyInvite = async (family: FamilySpace) => {
    const text = `${family.name} 가족 앨범 초대\n${inviteLink(family.inviteCode)}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(family.id);
      setTimeout(() => setCopiedId(''), 2000);
    } catch {
      setError('링크를 자동으로 복사하지 못했습니다. 아래 링크를 길게 눌러 복사해 주세요.');
    }
  };

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setError('가족 공간 이름을 2자 이상 입력해 주세요.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const family = await adminService.createFamily(trimmed);
      setFamilies((current) => [...current, family]);
      setName('');
      setMessage(`${family.name} 가족 공간을 만들었습니다. 아래 초대 링크를 전달해 주세요.`);
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : '가족 공간을 만들지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (!user?.isOwner) return <Navigate to="/forbidden" replace />;

  return (
    <div className="min-h-screen bg-background-50 pb-10">
      <header className="px-4 pt-12 pb-6 flex items-start justify-between gap-3">
        <div>
          <span className="text-xs text-accent-500 font-semibold tracking-wide uppercase">전체 관리자</span>
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mt-1">가족 공간 관리</h1>
          <p className="text-sm text-foreground-500 mt-2">가족별 사진·앨범·초대코드를 완전히 분리합니다.</p>
        </div>
        <Link to="/admin" className="flex-shrink-0 px-4 py-2 rounded-full bg-secondary-100 text-sm font-medium text-foreground-700">
          관리자 홈
        </Link>
      </header>

      <section className="px-4 pb-4">
        <div className="rounded-2xl bg-violet-50 border border-violet-100 p-4">
          <p className="text-sm font-semibold text-violet-800">{slotsText}</p>
          <p className="text-xs text-violet-700 mt-1 leading-relaxed">새 가족의 첫 가입자는 자동으로 그 가족의 관리자가 됩니다.</p>
        </div>
      </section>

      {error && <div className="mx-4 mb-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}
      {message && <div className="mx-4 mb-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{message}</div>}

      <section className="px-4 pb-5">
        {canCreate ? (
          <form onSubmit={create} className="bg-white rounded-2xl p-4 shadow-sm">
            <label className="block text-sm font-semibold text-foreground-900 mb-2" htmlFor="family-name">새 가족 공간 이름</label>
            <div className="flex gap-2">
              <input id="family-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="예: 김철수 가족" maxLength={40}
                className="min-w-0 flex-1 rounded-xl border border-secondary-200 px-3 py-3 text-sm outline-none focus:border-accent-400" />
              <button type="submit" disabled={saving} className="rounded-xl bg-accent-500 px-4 text-sm font-semibold text-white disabled:opacity-50">
                {saving ? '생성 중' : '생성'}
              </button>
            </div>
          </form>
        ) : (
          <div className="rounded-2xl bg-secondary-100 px-4 py-4 text-sm text-foreground-600">최대 3개 가족 공간을 모두 사용 중입니다.</div>
        )}
      </section>

      <section className="px-4 space-y-3">
        {loading && <div className="rounded-2xl bg-white p-6 text-center text-sm text-foreground-400">불러오는 중...</div>}
        {!loading && families.map((family) => (
          <article key={family.id} className="rounded-2xl bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-bold text-foreground-950">{family.name}</h2>
                <p className="text-xs text-foreground-500 mt-1">사용자 {family.userCount}명 · 관리자 {family.adminCount}명 · 앨범 {family.albumCount}개 · 사진 {family.photoCount}개</p>
              </div>
              <span className="rounded-full bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-700">가족 {family.id}</span>
            </div>
            <div className="mt-4 rounded-xl bg-secondary-50 px-3 py-3">
              <p className="text-[11px] text-foreground-500">가족 전용 초대 링크</p>
              <p className="mt-1 break-all text-xs text-foreground-700 select-all">{inviteLink(family.inviteCode)}</p>
            </div>
            <button onClick={() => void copyInvite(family)} className="mt-3 w-full rounded-xl bg-secondary-100 py-3 text-sm font-semibold text-foreground-800">
              {copiedId === family.id ? '초대 링크 복사 완료' : '초대 링크 복사'}
            </button>
          </article>
        ))}
      </section>
    </div>
  );
}
